import type {FC} from 'react';
import {Dialog, DialogTitle, DialogContent, DialogActions} from '@mui/material';
import {Button, Stack, Typography} from '@wellstone-solutions/web';
import {Theme} from '@wellstone-solutions/common';

type PropsType = {
  isEditMode: boolean;
  open: boolean;
  setOpen: (boolean) => void;
  onConfirm: () => Promise<void>;
};

export const ConfirmDisable: FC<PropsType> = ({
  isEditMode,
  open,
  setOpen,
  onConfirm,
}) => {
  const onDialogClose = () => {
    setOpen(false);
  };

  /**
   * There are a few modes for this dialog:
   * 1. If form has initial values, then we're in Edit mode, and disabling will need to send DELETE request
   * 2. If no initial values, then we're in Create mode, and disabling should do one of two things:
   *   a. If the form is dirty, then it should warn the user that they will lose their changes
   *   b. If the form is not dirty, then it should just close the dialog without any action
   */

  const headerText = isEditMode ? 'Remove Group Schedule?' : 'Are you sure?';

  const subHeadingText = isEditMode
    ? "Turning this off will remove the Group event from everyone's calendar that belongs to this group."
    : 'You have unsaved changes on this page.';

  return (
    <Dialog
      onClose={onDialogClose}
      open={open}
      data-testid="document-revert-confirm"
      fullWidth>
      <DialogTitle>
        <Typography sx={styles.title} variant="h5">
          {headerText}
        </Typography>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <Typography sx={styles.text}>{subHeadingText}</Typography>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Stack
          direction="row"
          sx={{
            paddingTop: 0,
            width: '100%',
            justifyContent: 'end',
          }}>
          <Button onClick={onDialogClose} color="primary" style={styles.button}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onDialogClose();
              onConfirm();
            }}
            color="primary"
            variant="contained"
            style={styles.button}>
            {isEditMode ? 'Remove' : 'Continue'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const styles = {
  content: {
    paddingBottom: Theme.spacing.micro,
  },
  text: {
    fontSize: 18,
  },
  actions: {
    paddingLeft: Theme.spacing.small,
    paddingBottom: 6,
    justifyContent: 'start',
  },
  headerBar: {
    position: 'absolute',
    height: Theme.spacing.average,
    top: 0,
    left: 0,
    backgroundColor: Theme.colorPalette.primary,
    width: '100%',
  },
  button: {
    fontSize: 16,
    textTransform: 'none',
    borderRadius: '20px',
    paddingLeft: Theme.spacing.huge,
    paddingRight: Theme.spacing.huge,
    marginRight: Theme.spacing.average,
  },
  title: {
    mt: 2,
    fontSize: 24,
    color: Theme.colorPalette.darkest,
    fontWeight: 'bold',
  },
};
